import { useEffect, useMemo, useState } from "react";
import DownloadIcon from "@mui/icons-material/Download";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import FormControlLabel from "@mui/material/FormControlLabel";
import MenuItem from "@mui/material/MenuItem";
import Switch from "@mui/material/Switch";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { alpha } from "@mui/material/styles";
import { Link } from "react-router-dom";
import { Confirm, useGetList, useNotify, usePermissions, useRefresh } from "react-admin";
import {
  CsvImportDialog,
  CsvImportTriggerButton,
  useCsvImportDialogState,
} from "@/components/csv-import";
import { callApi } from "@/dataprovider/misc-apis";
import { hasPermission } from "@/utils/permission-utils";
import { apiErrorMessage } from "../../shared/api-error";
import {
  MADRASA_ENROLLMENTS_CREATE,
  MADRASA_STUDENTS_CREATE,
} from "../../shared/madrasa-permissions";
import {
  buildProblemRowsCsv,
  downloadTextFile,
  parseStudentImportFile,
  STUDENT_IMPORT_TEMPLATE_CSV,
  type ImportRowResult,
  type ParsedStudentImport,
} from "./student-import-parse";

type Summary = {
  total: number;
  createStudent: number;
  enrollOnly: number;
  skipExists: number;
  errors: number;
  warnings: number;
  unknownClasses: string[];
};
type PreviewResponse = {
  academicYear: { id: string; name: string } | null;
  summary: Summary;
  rows: ImportRowResult[];
};
type ApplyResponse = PreviewResponse & {
  created: number;
  enrolled: number;
  skipped: number;
  failed: number;
};
type YearRow = { id: string; name: string; isCurrent: boolean; isClosed: boolean };

const FILE_ACCEPT =
  ".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel";

const ACTION_LABEL: Record<ImportRowResult["action"], string> = {
  CREATE_STUDENT: "Create",
  ENROLL_ONLY: "Enroll only",
  SKIP_EXISTS: "Already exists",
  ERROR: "Problem",
};

function actionLabel(row: ImportRowResult): string {
  return row.action === "CREATE_STUDENT" && row.className
    ? "Create + enroll"
    : ACTION_LABEL[row.action];
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

function confirmText(preview: PreviewResponse | null): string {
  if (!preview) return "";
  const { createStudent, enrollOnly, errors } = preview.summary;
  const created = `${plural(createStudent, "student", "students")} will be created`;
  const enrolled = enrollOnly
    ? ` and ${plural(enrollOnly, "existing student", "existing students")} enrolled`
    : "";
  const skipped = errors
    ? ` ${plural(errors, "row with a problem is", "rows with problems are")} skipped.`
    : "";
  return `${created}${enrolled}.${skipped} Either everything valid is saved or nothing is.`;
}

function downloadTemplate() {
  downloadTextFile("madrasa-students-import-template.csv", STUDENT_IMPORT_TEMPLATE_CSV);
}

/**
 * Import students (and optionally their class enrollment) from CSV/Excel.
 * Always a two-step flow: the server previews every row, and nothing is written until confirmed.
 */
export function StudentImportButton() {
  const notify = useNotify();
  const refresh = useRefresh();
  const { permissions } = usePermissions();
  // The import creates students AND enrollments, so it needs both (the API enforces the same).
  const canImport =
    hasPermission(permissions, MADRASA_STUDENTS_CREATE) &&
    hasPermission(permissions, MADRASA_ENROLLMENTS_CREATE);

  const { open, busy, setBusy, inputRef, openDialog, closeDialog, chooseFile } =
    useCsvImportDialogState();
  const years = useGetList<YearRow>("academicYears", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "startDate", order: "DESC" },
  });
  const openYears = useMemo(() => (years.data ?? []).filter((y) => !y.isClosed), [years.data]);

  const [yearId, setYearId] = useState("");
  const [fileLabel, setFileLabel] = useState("");
  const [parsed, setParsed] = useState<ParsedStudentImport | null>(null);
  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  const [result, setResult] = useState<ApplyResponse | null>(null);
  const [fatal, setFatal] = useState<string | null>(null);
  const [onlyIssues, setOnlyIssues] = useState(false);
  const [confirming, setConfirming] = useState(false);

  // Default to the current academic year once the years have loaded.
  useEffect(() => {
    if (!yearId && openYears.length > 0) {
      setYearId((openYears.find((y) => y.isCurrent) ?? openYears[0]).id);
    }
  }, [openYears, yearId]);

  if (!canImport) return null;

  const reset = () => {
    setFileLabel("");
    setParsed(null);
    setPreview(null);
    setResult(null);
    setFatal(null);
    setOnlyIssues(false);
  };

  const handleClose = () => {
    if (busy) return;
    closeDialog();
    reset();
  };

  const requestBody = (p: ParsedStudentImport, year: string) => ({
    ...(year ? { academicYearId: year } : {}),
    rows: p.rows,
  });

  const runPreview = async (p: ParsedStudentImport, year: string) => {
    setBusy(true);
    setResult(null);
    setFatal(null);
    try {
      const response = await callApi({
        location: "madrasaStudents/import-preview",
        method: "POST",
        data: requestBody(p, year),
      });
      setPreview(response.data as PreviewResponse);
    } catch (error: unknown) {
      setPreview(null);
      setFatal(apiErrorMessage(error, "Could not preview the file"));
    } finally {
      setBusy(false);
    }
  };

  const onFileSelected = async (file: File) => {
    setBusy(true);
    reset();
    setFileLabel(file.name);
    try {
      const p = await parseStudentImportFile(file);
      setParsed(p);
      await runPreview(p, yearId);
    } catch (error: unknown) {
      setFatal(error instanceof Error ? error.message : "Invalid file");
      setBusy(false);
    }
  };

  const onYearChange = (value: string) => {
    setYearId(value);
    // Class/enrollment outcomes depend on the year, so re-check the same file against it.
    if (parsed) void runPreview(parsed, value);
  };

  const runApply = async () => {
    if (!parsed) return;
    setConfirming(false);
    setBusy(true);
    try {
      const response = await callApi({
        location: "madrasaStudents/import-apply",
        method: "POST",
        data: requestBody(parsed, yearId),
      });
      const data = response.data as ApplyResponse;
      setResult(data);
      setPreview(null);
      const enrolledPart = data.enrolled
        ? `, ${plural(data.enrolled, "enrollment", "enrollments")}`
        : "";
      const failedPart = data.failed
        ? `. ${plural(data.failed, "row", "rows")} had problems.`
        : ".";
      notify(
        `Imported ${plural(data.created, "student", "students")}${enrolledPart}${failedPart}`,
        { type: data.created + data.enrolled > 0 ? "success" : "info" }
      );
      refresh();
    } catch (error: unknown) {
      notify(apiErrorMessage(error, "Import failed"), { type: "error" });
    } finally {
      setBusy(false);
    }
  };

  const shown = preview ?? result;
  const summary = shown?.summary;
  const writable = preview ? preview.summary.createStudent + preview.summary.enrollOnly : 0;
  const problemRows = (shown?.rows ?? []).filter((r) => r.action === "ERROR");
  const visibleRows = (shown?.rows ?? []).filter(
    (r) => !onlyIssues || r.errors.length > 0 || r.warnings.length > 0
  );

  const downloadProblems = () => {
    if (!parsed || !shown) return;
    downloadTextFile(
      "madrasa-students-rows-to-fix.csv",
      buildProblemRowsCsv(parsed.headers, parsed.rawRows, shown.rows)
    );
  };

  return (
    <>
      <Button
        startIcon={<DownloadIcon />}
        onClick={downloadTemplate}
        size="small"
        variant="outlined"
      >
        Download template
      </Button>
      <CsvImportTriggerButton
        label="Import CSV / Excel"
        onClick={() => {
          reset();
          openDialog();
        }}
      />

      <CsvImportDialog
        open={open}
        onClose={handleClose}
        busy={busy}
        maxWidth="lg"
        title="Import students"
        progress={busy || undefined}
        fileInputRef={inputRef}
        accept={FILE_ACCEPT}
        onFileSelected={onFileSelected}
        actions={
          <>
            <Button onClick={handleClose} disabled={busy}>
              Close
            </Button>
            <Button variant="outlined" disabled={busy} onClick={downloadTemplate}>
              Download template
            </Button>
            {problemRows.length > 0 && (
              <Button variant="outlined" disabled={busy} onClick={downloadProblems}>
                Download rows to fix ({problemRows.length})
              </Button>
            )}
            <Button variant="outlined" disabled={busy} onClick={chooseFile}>
              {parsed ? "Choose another file" : "Choose CSV / Excel file"}
            </Button>
            <Button
              variant="contained"
              disabled={busy || writable === 0}
              onClick={() => setConfirming(true)}
            >
              {writable > 0 ? `Import ${plural(writable, "row", "rows")}` : "Import"}
            </Button>
          </>
        }
      >
        <Alert severity="info" sx={{ mb: 2 }}>
          Choose a file, review what would happen to every row, then confirm. Nothing is saved until
          you confirm. Students are matched by ITS, so uploading the same file again is safe:
          existing students are skipped and never overwritten. Imported enrollments are not treated
          as new admissions unless the <code>new_admission</code> column says <strong>yes</strong>.
        </Alert>

        <TextField
          select
          label="Academic year for class enrollments"
          value={yearId}
          onChange={(e) => onYearChange(e.target.value)}
          disabled={busy || openYears.length === 0}
          helperText="Only used when the file has a class column. Students without a class are just added to the registry."
          sx={{ minWidth: 320, mb: 2 }}
        >
          {openYears.map((y) => (
            <MenuItem key={y.id} value={y.id}>
              {y.name}
              {y.isCurrent ? " (current)" : ""}
            </MenuItem>
          ))}
        </TextField>

        {fileLabel && (
          <Typography variant="body2" sx={{ mb: 1 }}>
            File: <strong>{fileLabel}</strong>
            {parsed && parsed.skippedBlankRows > 0
              ? ` (${parsed.skippedBlankRows} blank rows ignored)`
              : ""}
          </Typography>
        )}
        {parsed && parsed.unknownHeaders.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            These columns are not recognised and were ignored: {parsed.unknownHeaders.join(", ")}.
          </Alert>
        )}
        {fatal && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {fatal}
          </Alert>
        )}

        {summary && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1 }}>
              <Chip
                label={`${summary.createStudent} to create`}
                color="success"
                variant="outlined"
              />
              <Chip label={`${summary.enrollOnly} enroll only`} color="info" variant="outlined" />
              <Chip label={`${summary.skipExists} already exist`} variant="outlined" />
              <Chip
                label={`${summary.errors} with problems`}
                color={summary.errors ? "error" : "default"}
                variant="outlined"
              />
              {summary.warnings > 0 && (
                <Chip
                  label={`${summary.warnings} with warnings`}
                  color="warning"
                  variant="outlined"
                />
              )}
            </Box>
            {result && (
              <Alert severity="success" sx={{ mb: 1 }}>
                Imported {plural(result.created, "student", "students")} and{" "}
                {plural(result.enrolled, "enrollment", "enrollments")}.{" "}
                {result.skipped > 0 && `${plural(result.skipped, "row", "rows")} already existed. `}
                {result.failed > 0 &&
                  `${plural(result.failed, "row", "rows")} had problems and were not imported: download them, fix them and upload again.`}{" "}
                <Link to="/madrasaStudents" onClick={handleClose}>
                  View students
                </Link>
              </Alert>
            )}
            {summary.unknownClasses.length > 0 && (
              <Alert severity="warning" sx={{ mb: 1 }}>
                These classes in the file do not exist yet:{" "}
                <strong>{summary.unknownClasses.join(", ")}</strong>.{" "}
                <Link to="/madrasaClasses/create" onClick={handleClose}>
                  Create the classes
                </Link>
                , then choose the file again.
              </Alert>
            )}
            <FormControlLabel
              control={<Switch checked={onlyIssues} onChange={(_, v) => setOnlyIssues(v)} />}
              label="Show only rows with problems or warnings"
            />
          </Box>
        )}

        {shown && (
          <TableContainer sx={{ maxHeight: 380 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Row</TableCell>
                  <TableCell>ITS ID</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>What happens</TableCell>
                  <TableCell>Class</TableCell>
                  <TableCell>Notes</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleRows.map((row) => {
                  const bad = row.action === "ERROR";
                  return (
                    <TableRow
                      key={row.rowNumber}
                      sx={(theme) =>
                        bad
                          ? {
                              bgcolor: alpha(
                                theme.palette.error.main,
                                theme.palette.mode === "dark" ? 0.22 : 0.12
                              ),
                            }
                          : {}
                      }
                    >
                      <TableCell>{row.rowNumber}</TableCell>
                      <TableCell>{row.itsId || "—"}</TableCell>
                      <TableCell>{row.name || "—"}</TableCell>
                      <TableCell>
                        {actionLabel(row)}
                        {row.admissionNo != null ? ` (adm. ${row.admissionNo})` : ""}
                      </TableCell>
                      <TableCell>
                        {row.className
                          ? row.section
                            ? `${row.className} (${row.section})`
                            : row.className
                          : "—"}
                      </TableCell>
                      <TableCell>
                        {row.errors.map((m) => (
                          <Typography key={m} variant="body2" color="error">
                            {m}
                          </Typography>
                        ))}
                        {row.warnings.map((m) => (
                          <Typography key={m} variant="body2" sx={{ color: "warning.main" }}>
                            {m}
                          </Typography>
                        ))}
                        {row.note && (
                          <Typography variant="body2" color="text.secondary">
                            {row.note}
                          </Typography>
                        )}
                        {!row.errors.length && !row.warnings.length && !row.note && "Ready"}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CsvImportDialog>

      <Confirm
        isOpen={confirming}
        loading={busy}
        title={`Import ${plural(writable, "row", "rows")}?`}
        content={confirmText(preview)}
        onConfirm={runApply}
        onClose={() => setConfirming(false)}
      />
    </>
  );
}
